import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { INCOMING_STATUS, UPLOAD_BASE, fmtDateTH, fmtDateTimeTH } from './aoShared'

export default function IncomingDocDetail() {
  const { inId } = useParams()
  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Forward (เกษียน/เวียน) modal
  const [showForward, setShowForward] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [note, setNote] = useState('')
  const [filter, setFilter] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/incoming/${inId}`)
      if (res.data.success) setDoc(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [inId])

  useEffect(() => { fetchData() }, [fetchData])

  const openForward = async () => {
    setModalError('')
    setNote('')
    setSelected(new Set())
    setFilter('')
    setShowForward(true)
    if (!teachers.length) {
      try {
        const res = await axiosInstance.get('/api/ao/teachers')
        if (res.data.success) setTeachers(res.data.data)
      } catch (err) {
        setModalError('โหลดรายชื่อบุคลากรไม่สำเร็จ')
      }
    }
  }

  const alreadyRouted = useMemo(() => new Set((doc?.routes || []).map(r => r.rt_to_t_id)), [doc])

  const departments = useMemo(() => {
    const set = new Set(teachers.map(t => t.hp_department).filter(Boolean))
    return [...set].sort()
  }, [teachers])

  const visibleTeachers = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return teachers.filter(t =>
      !f || t.t_name.toLowerCase().includes(f) || (t.hp_department || '').toLowerCase().includes(f))
  }, [teachers, filter])

  const toggle = (tid) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(tid)) next.delete(tid)
    else next.add(tid)
    return next
  })

  const selectAll = () => setSelected(new Set(teachers.filter(t => !alreadyRouted.has(t.t_id)).map(t => t.t_id)))
  const selectDepartment = (dep) => setSelected(prev => {
    const next = new Set(prev)
    teachers.filter(t => t.hp_department === dep && !alreadyRouted.has(t.t_id)).forEach(t => next.add(t.t_id))
    return next
  })

  const handleForward = async (e) => {
    e.preventDefault()
    if (!selected.size) {
      setModalError('กรุณาเลือกผู้รับอย่างน้อย 1 คน')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post(`/api/ao/incoming/${inId}/forward`, {
        recipient_t_ids: [...selected],
        note,
      })
      if (res.data.success) {
        setShowForward(false)
        Swal.fire({ icon: 'success', title: `ส่งต่อให้ ${res.data.data.forwarded} คนเรียบร้อย`, timer: 1800, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'ส่งต่อไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleStatus = async (status, label) => {
    const result = await Swal.fire({
      icon: 'question',
      title: `เปลี่ยนสถานะเป็น "${label}"?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/incoming/${inId}/status`, { status })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เปลี่ยนสถานะไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const st = doc ? (INCOMING_STATUS[doc.in_status] || INCOMING_STATUS.pending) : null

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <Link to="/admin-office/incoming" className="text-decoration-none me-2" title="กลับทะเบียนหนังสือรับ">
                <i className="ri-arrow-left-line" />
              </Link>
              <i className="ri-inbox-archive-line me-2 text-primary" />
              หนังสือรับ {doc?.in_full_no || ''}
            </h4>
            {doc && (
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={openForward}>
                  <i className="ri-share-forward-line me-1" />เกษียน/เวียนหนังสือ
                </Button>
                {doc.in_status !== 'done' && (
                  <Button variant="outline-success" size="sm" onClick={() => handleStatus('done', 'เสร็จสิ้น')}>
                    <i className="ri-check-double-line me-1" />ปิดเรื่อง
                  </Button>
                )}
              </div>
            )}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : !doc ? (
            <Alert variant="warning">ไม่พบหนังสือรับ</Alert>
          ) : (
            <Row className="g-3">
              <Col lg={5}>
                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">รายละเอียดหนังสือ</Card.Title>
                    <Badge bg={st.bg}>{st.label}</Badge>
                  </Card.Header>
                  <Card.Body className="fs-sm">
                    <Table borderless size="sm" className="mb-0">
                      <tbody>
                        <tr><td className="text-muted" style={{ width: 130 }}>เลขทะเบียนรับ</td><td className="fw-semibold">{doc.in_full_no}</td></tr>
                        <tr><td className="text-muted">วันที่ลงรับ</td><td>{fmtDateTH(doc.in_received_date)}</td></tr>
                        <tr><td className="text-muted">เลขที่หนังสือ</td><td>{doc.in_doc_no || '-'}</td></tr>
                        <tr><td className="text-muted">ลงวันที่</td><td>{fmtDateTH(doc.in_doc_date)}</td></tr>
                        <tr><td className="text-muted">จาก</td><td>{doc.in_source}</td></tr>
                        <tr><td className="text-muted">เรียน</td><td>{doc.in_to || '-'}</td></tr>
                        <tr><td className="text-muted">เรื่อง</td><td className="fw-semibold">{doc.in_subject}</td></tr>
                        {doc.in_detail && <tr><td className="text-muted">รายละเอียด</td><td style={{ whiteSpace: 'pre-wrap' }}>{doc.in_detail}</td></tr>}
                        <tr>
                          <td className="text-muted">ไฟล์แนบ</td>
                          <td>
                            {doc.in_file ? (
                              <a href={`${UPLOAD_BASE}/uploads/ao_docs/${doc.in_file}`} target="_blank" rel="noreferrer">
                                <i className="ri-attachment-2 me-1" />เปิดไฟล์สแกน
                              </a>
                            ) : '-'}
                          </td>
                        </tr>
                        <tr><td className="text-muted">ผู้ลงรับ</td><td>{doc.created_by_name || '-'} · {fmtDateTimeTH(doc.created_at)}</td></tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">
                      การเกษียน/เวียนหนังสือ
                      {doc.routes.length > 0 && (
                        <span className="text-muted fw-normal ms-2 fs-sm">
                          รับทราบแล้ว {doc.routes.filter(r => r.rt_ack).length}/{doc.routes.length} คน
                        </span>
                      )}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {doc.routes.length === 0 ? (
                      <div className="text-center text-muted py-4">ยังไม่ได้ส่งต่อหนังสือฉบับนี้</div>
                    ) : (
                      <Table responsive hover className="mb-0 fs-sm">
                        <thead className="table-light">
                          <tr>
                            <th>ผู้รับ</th>
                            <th>ฝ่าย</th>
                            <th>ข้อความเกษียน</th>
                            <th style={{ width: 150 }}>รับทราบ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doc.routes.map(r => (
                            <tr key={r.rt_id}>
                              <td>{r.recipient_name}</td>
                              <td>{r.hp_department || '-'}</td>
                              <td className="text-truncate" style={{ maxWidth: 200 }} title={r.rt_note || ''}>{r.rt_note || '-'}</td>
                              <td>
                                {r.rt_ack ? (
                                  <>
                                    <Badge bg="success"><i className="ri-check-line" /> รับทราบ</Badge>
                                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>{fmtDateTimeTH(r.rt_ack_at)}</div>
                                    {r.rt_ack_note && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{r.rt_ack_note}</div>}
                                  </>
                                ) : (
                                  <Badge bg="warning">รอรับทราบ</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <Footer />

      {/* Forward modal */}
      <Modal show={showForward} onHide={() => setShowForward(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">เกษียน/เวียนหนังสือ — เลือกผู้รับ</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleForward}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ข้อความเกษียน <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} placeholder="เช่น เพื่อทราบและดำเนินการในส่วนที่เกี่ยวข้อง"
                value={note} onChange={e => setNote(e.target.value)} />
            </Form.Group>
            <div className="d-flex gap-2 mb-2 flex-wrap align-items-center">
              <Button variant="outline-primary" size="sm" onClick={selectAll}>
                <i className="ri-team-line me-1" />ทั้งโรงเรียน
              </Button>
              {departments.map(dep => (
                <Button key={dep} variant="outline-secondary" size="sm" onClick={() => selectDepartment(dep)}>
                  {dep}
                </Button>
              ))}
              <span className="ms-auto fs-sm text-muted">เลือกแล้ว {selected.size} คน</span>
            </div>
            <Form.Control size="sm" className="mb-2" placeholder="ค้นหาชื่อ/ฝ่าย"
              value={filter} onChange={e => setFilter(e.target.value)} />
            <div className="border rounded" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {visibleTeachers.map(t => {
                const routed = alreadyRouted.has(t.t_id)
                return (
                  <Form.Check key={t.t_id} type="checkbox" className="px-3 py-1 border-bottom ms-3"
                    id={`fwd-t-${t.t_id}`}
                    label={<span className={routed ? 'text-muted' : ''}>{t.t_name}{t.hp_department ? ` · ${t.hp_department}` : ''}{routed ? ' (ส่งแล้ว)' : ''}</span>}
                    checked={selected.has(t.t_id)}
                    disabled={routed}
                    onChange={() => toggle(t.t_id)} />
                )
              })}
              {!visibleTeachers.length && <div className="text-center text-muted py-3 fs-sm">ไม่พบบุคลากร</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowForward(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-share-forward-line me-1" />}
              ส่งต่อ ({selected.size} คน)
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
